import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, Search, Bell, Sun, Moon, LogOut, User as UserIcon, ChevronRight } from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

const ROUTE_LABELS = {
  dashboard: "Dashboard",
  users: "Users",
  "devotional-types": "Devotional Types",
  devotionals: "Devotionals",
  bible: "Bible",
  settings: "Settings",
  "admin-users": "Admin Users",
};

function Breadcrumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split("/").filter(Boolean);
  return (
    <nav data-testid="breadcrumbs" className="hidden sm:flex items-center text-sm text-muted-foreground min-w-0">
      <Link to="/dashboard" className="hover:text-foreground transition-colors whitespace-nowrap">
        Home
      </Link>
      {segments.map((seg, i) => {
        const path = "/" + segments.slice(0, i + 1).join("/");
        const isLast = i === segments.length - 1;
        const label = ROUTE_LABELS[seg] || seg.replace(/-/g, " ");
        return (
          <span key={path} className="flex items-center min-w-0">
            <ChevronRight className="size-3.5 mx-1 shrink-0 text-muted-foreground/50" />
            {isLast ? (
              <span className="text-foreground font-medium capitalize truncate">{label}</span>
            ) : (
              <Link to={path} className="hover:text-foreground transition-colors capitalize truncate">
                {label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function ThemeToggle() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const current = theme === "system" ? resolvedTheme : theme;
  return (
    <button
      type="button"
      onClick={() => setTheme(current === "dark" ? "light" : "dark")}
      data-testid="theme-toggle"
      className="size-9 inline-flex items-center justify-center rounded-lg border border-border bg-background hover:bg-muted transition-colors"
      aria-label="Toggle theme"
    >
      {current === "dark" ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
    </button>
  );
}

function UserMenu() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (admin?.name || admin?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="user-menu-trigger"
          className="flex items-center gap-2 rounded-lg border border-border bg-background hover:bg-muted transition-colors pl-1 pr-2.5 py-1"
        >
          <div className="size-7 rounded-full bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center text-xs font-semibold">
            {initials}
          </div>
          <div className="hidden sm:flex flex-col items-start leading-tight">
            <span className="text-xs font-medium truncate max-w-[140px]">{admin?.name || "Admin"}</span>
            <span className="text-[10px] text-muted-foreground truncate max-w-[140px]">
              {admin?.role?.replace("_", " ")}
            </span>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-semibold">{admin?.name || "Admin"}</span>
            <span className="text-xs text-muted-foreground font-normal truncate">{admin?.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/settings/account")} data-testid="menu-item-settings">
          <UserIcon className="size-4 mr-2" /> Account Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            logout();
            navigate("/login");
          }}
          data-testid="menu-item-logout"
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="size-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TopBar({ onMenuClick }) {
  const [query, setQuery] = useState("");
  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-30 h-16 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70"
    >
      <div className="h-full flex items-center gap-2 sm:gap-4 px-3 sm:px-6">
        <button
          type="button"
          onClick={onMenuClick}
          data-testid="topbar-hamburger"
          className="md:hidden size-9 inline-flex items-center justify-center rounded-lg border border-border bg-background hover:bg-muted transition-colors"
          aria-label="Open menu"
        >
          <Menu className="size-[18px]" />
        </button>

        <div className="flex-1 min-w-0">
          <Breadcrumbs />
        </div>

        <div className="hidden lg:flex items-center w-72 relative">
          <Search className="absolute left-3 size-4 text-muted-foreground pointer-events-none" />
          <Input
            data-testid="topbar-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="h-9 pl-9"
          />
        </div>

        <button
          type="button"
          data-testid="topbar-notifications"
          className="relative size-9 inline-flex items-center justify-center rounded-lg border border-border bg-background hover:bg-muted transition-colors"
          aria-label="Notifications"
        >
          <Bell className="size-[18px]" />
          <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-accent ring-2 ring-background" />
        </button>

        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
